import { PageShell } from "../components/PageShell";
import { PageHero } from "../components/PageHero";
import { LAST_UPDATED, EMAIL, COMPANY_NAME } from "../data/site";

const LegalPage = ({ eyebrow, title, italic, sections, subtitle, testId }) => (
  <PageShell>
    <PageHero eyebrow={eyebrow} title={title} italic={italic} subtitle={subtitle} />
    <section className="py-16 md:py-20 px-6 md:px-12" data-testid={testId}>
      <div className="max-w-3xl mx-auto space-y-10 text-white/70 leading-relaxed">
        <p className="text-[10px] tracking-[0.3em] uppercase text-white/40">
          Last updated · {LAST_UPDATED}
        </p>
        {sections.map((s, i) => (
          <div key={i}>
            <h2 className="font-serif-display text-2xl md:text-3xl text-white mb-4">{s.h}</h2>
            {s.p.map((para, j) => (
              <p key={j} className="mb-4 text-[15px]">{para}</p>
            ))}
          </div>
        ))}
        <div className="pt-6 border-t border-white/10 text-sm text-white/50">
          Questions? Please contact us at{" "}
          <a href={`mailto:${EMAIL}`} className="text-[#c5a059] hover:underline">{EMAIL}</a>.
        </div>
      </div>
    </section>
  </PageShell>
);

export function PrivacyPolicy() {
  return (
    <LegalPage
      eyebrow="Legal"
      title="Privacy"
      italic="Policy"
      subtitle="How we collect, use and protect the personal information you share with us."
      testId="privacy-policy"
      sections={[
        { h: "1. Information we collect", p: [
          `${COMPANY_NAME} collects the information you voluntarily provide through our consultation forms, WhatsApp chats and email correspondence — including your name, email address, phone number, country of residence, budget range, property preferences and any notes you choose to share.`,
          "We also collect limited technical information (IP address, browser type, page interactions) via cookies and analytics tools to understand how our site is used and improve it.",
        ]},
        { h: "2. How we use your information", p: [
          "Your information is used solely to respond to your enquiry, share relevant property information, coordinate viewings and reservations, and — with your consent — send occasional market updates.",
          "We do not sell, rent or trade your personal information. It is shared only with regulated third parties strictly necessary to fulfil your enquiry (e.g. the specific developer you have expressed interest in, or a mortgage broker if you request one).",
        ]},
        { h: "3. Cookies", p: [
          "We use essential cookies to make the site function and analytics cookies to understand aggregated usage. You may accept or decline non-essential cookies via the cookie banner. Blocking non-essential cookies will not prevent you from using the site.",
        ]},
        { h: "4. Data retention & security", p: [
          "We retain enquiry data for as long as necessary to service your interest and for regulatory record-keeping, after which it is deleted. Data is stored on secured infrastructure with access limited to authorised advisory staff.",
        ]},
        { h: "5. Your rights", p: [
          "You may request access to, correction of, or deletion of your personal data at any time by emailing us. We will respond within a reasonable timeframe.",
        ]},
        { h: "6. Third-party links", p: [
          "The site may contain links to developer sites, Google Maps, social media and other third-party resources. Their content and privacy practices are outside our control.",
        ]},
      ]}
    />
  );
}

export function Terms() {
  return (
    <LegalPage
      eyebrow="Legal"
      title="Terms &"
      italic="Conditions"
      subtitle="The terms governing your use of this website and our advisory services."
      testId="terms-conditions"
      sections={[
        { h: "1. Nature of information", p: [
          "The content of this website is provided for general informational purposes only and does not constitute legal, financial, tax or investment advice. Property specifications, prices, availability, payment plans and handover dates are subject to change and must be confirmed directly with the relevant developer.",
        ]},
        { h: "2. No guaranteed outcomes", p: [
          `${COMPANY_NAME} makes no representations of guaranteed rental income, guaranteed capital appreciation, risk-free returns or any similar assurance. All yield figures are estimates based on prevailing market conditions and may vary materially.`,
        ]},
        { h: "3. Advisory relationship", p: [
          "A formal advisory relationship only commences once agreed in writing. Enquiries, WhatsApp chats and preliminary calls do not, in themselves, create a fiduciary or binding relationship.",
        ]},
        { h: "4. Intellectual property", p: [
          "All content on this site (text, imagery, branding, layout) is the property of Universal Dream Properties LLC or its licensors and may not be reproduced without written permission.",
        ]},
        { h: "5. Governing law", p: [
          "These terms are governed by the laws of the United Arab Emirates and the Emirate of Dubai. Any disputes shall be subject to the exclusive jurisdiction of the courts of Dubai.",
        ]},
      ]}
    />
  );
}

export function InvestmentDisclaimer() {
  return (
    <LegalPage
      eyebrow="Legal"
      title="Investment"
      italic="Disclaimer"
      subtitle="Important disclosures every investor should read before acting on information from this site."
      testId="investment-disclaimer"
      sections={[
        { h: "1. Estimates are not guarantees", p: [
          "All rental yield figures displayed on this site are labelled as Estimated Gross Rental Yield or Projected Annual Rental Yield. These are indicative figures based on current market conditions and comparable transactions. They are not a guarantee of future rental income, occupancy, capital appreciation or resale value.",
          "Actual net returns will typically be lower than the headline gross yield after deducting service charges, maintenance, property management fees, vacancy allowances, financing costs and any applicable taxes in the investor's country of residence.",
        ]},
        { h: "2. Investment risks", p: [
          "Property is subject to market risk, vacancy risk, construction and handover risk, service charges, maintenance costs, financing costs, currency risk, resale-liquidity risk, regulatory changes and tax obligations. A dedicated discussion of these risks is provided in our Investment Guide.",
          `${COMPANY_NAME} does not use — and clients should be cautious of any counterparty who uses — language such as "guaranteed ROI", "guaranteed rental income", "risk-free investment", "guaranteed capital appreciation" or "no-risk property".`,
        ]},
        { h: "3. Not personal advice", p: [
          "Information on this site is general in nature and does not take into account the personal financial circumstances, objectives or tax position of any individual investor. You should obtain independent legal, tax and financial advice before making any property investment decision.",
        ]},
        { h: "4. Developer information", p: [
          "Prices, availability, payment plans, handover dates and project specifications are subject to change and must be confirmed with the relevant developer. Where third-party imagery and renderings are used, they are for illustrative purposes only.",
        ]},
        { h: "5. Regulatory framework", p: [
          "All property transactions in the Emirate of Dubai are subject to the regulations of the Dubai Land Department (DLD) and the Real Estate Regulatory Agency (RERA). Universal Dream Properties LLC operates under UAE brokerage licence #1410017.",
        ]},
      ]}
    />
  );
}
